import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppStoreAudience } from '@/hooks/useAppStoreAudience';
import StoreDownloadButtons from '@/components/StoreDownloadButtons';
import { APP_DOWNLOAD_DISMISS_KEY, APP_DOWNLOAD_DISMISS_MS, DOWNLOAD_PAGE_PATH } from '@/utils/appStores';

/** Homepage store CTA. Continue on website hides it for seven days. */
const GetTheAppPrompt = () => {
  const audience = useAppStoreAudience();
  const [dismissedAt, setDismissedAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(APP_DOWNLOAD_DISMISS_KEY);
    } catch {
      return null;
    }
  });

  if (audience === 'native') {
    return null;
  }
  if (audience === 'unknown') {
    return <div className="w-full max-w-2xl mt-6 min-h-[88px]" aria-hidden="true" />;
  }

  const dismissedMs = Number(dismissedAt);
  if (
    Number.isFinite(dismissedMs) &&
    dismissedMs > 0 &&
    Date.now() - dismissedMs < APP_DOWNLOAD_DISMISS_MS
  ) {
    return null;
  }

  const continueOnWebsite = () => {
    const timestamp = String(Date.now());
    setDismissedAt(timestamp);
    try {
      localStorage.setItem(APP_DOWNLOAD_DISMISS_KEY, timestamp);
    } catch {
      // Still hide for this visit.
    }
  };

  return (
    <aside
      className="w-full max-w-2xl mt-6 rounded-xl border border-blue-100 bg-blue-50 px-4 py-5 sm:px-5 text-center"
      aria-label="Get the QLS Global app"
    >
      <p className="font-semibold text-blue-900">Take QLS Global with you</p>
      <p className="mt-1 text-sm leading-relaxed text-blue-800">
        Search a place, get a Location Code, and share a map pin from the app.
      </p>
      <StoreDownloadButtons device={audience} className="mt-4" />
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <Link
          to={DOWNLOAD_PAGE_PATH}
          className="py-2 text-sm font-semibold text-[#1E3A8A] underline underline-offset-4"
        >
          See download options and QR code
        </Link>
        <button
          type="button"
          onClick={continueOnWebsite}
          className="min-h-11 py-2 text-sm text-slate-600 underline underline-offset-4"
        >
          Continue on website
        </button>
      </div>
    </aside>
  );
};

export default GetTheAppPrompt;
